/**
 * Oman's 11 governorates and their wilayats (EN/AR), used to place work locations (ADR-025, 01 §3).
 * Governorate codes are ISO 3166-2:OM subdivision codes without the `OM-` prefix; wilayat codes are
 * stable slugs, unique across the country. Stored on rows as codes, so a renamed place keeps its rows.
 * When the state adds a wilayat, append it here (never recode an existing one).
 */

export interface OmanWilayat {
  code: string
  en: string
  ar: string
}

export interface OmanGovernorate {
  code: string
  en: string
  ar: string
  wilayats: readonly OmanWilayat[]
}

export const OMAN_GOVERNORATES: readonly OmanGovernorate[] = [
  {
    code: 'MA',
    en: 'Muscat',
    ar: 'مسقط',
    wilayats: [
      { code: 'MUSCAT', en: 'Muscat', ar: 'مسقط' },
      { code: 'MUTTRAH', en: 'Muttrah', ar: 'مطرح' },
      { code: 'BAWSHAR', en: 'Bawshar', ar: 'بوشر' },
      { code: 'SEEB', en: 'As Seeb', ar: 'السيب' },
      { code: 'AMERAT', en: 'Al Amerat', ar: 'العامرات' },
      { code: 'QURAYYAT', en: 'Qurayyat', ar: 'قريات' },
    ],
  },
  {
    code: 'ZU',
    en: 'Dhofar',
    ar: 'ظفار',
    wilayats: [
      { code: 'SALALAH', en: 'Salalah', ar: 'صلالة' },
      { code: 'TAQAH', en: 'Taqah', ar: 'طاقة' },
      { code: 'MIRBAT', en: 'Mirbat', ar: 'مرباط' },
      { code: 'RAKHYUT', en: 'Rakhyut', ar: 'رخيوت' },
      { code: 'THUMRAIT', en: 'Thumrait', ar: 'ثمريت' },
      { code: 'DHALKUT', en: 'Dhalkut', ar: 'ضلكوت' },
      { code: 'MAZYUNAH', en: 'Al Mazyunah', ar: 'المزيونة' },
      { code: 'MUQSHIN', en: 'Muqshin', ar: 'مقشن' },
      { code: 'SHALIM', en: 'Shalim and the Hallaniyat Islands', ar: 'شليم وجزر الحلانيات' },
      { code: 'SADAH', en: 'Sadah', ar: 'سدح' },
    ],
  },
  {
    code: 'MU',
    en: 'Musandam',
    ar: 'مسندم',
    wilayats: [
      { code: 'KHASAB', en: 'Khasab', ar: 'خصب' },
      { code: 'DIBBA', en: 'Daba', ar: 'دبا' },
      { code: 'BUKHA', en: 'Bukha', ar: 'بخا' },
      { code: 'MADHA', en: 'Madha', ar: 'مدحاء' },
    ],
  },
  {
    code: 'BU',
    en: 'Al Buraimi',
    ar: 'البريمي',
    wilayats: [
      { code: 'BURAIMI', en: 'Al Buraimi', ar: 'البريمي' },
      { code: 'MAHDAH', en: 'Mahdah', ar: 'محضة' },
      { code: 'SUNAYNAH', en: 'As Sunaynah', ar: 'السنينة' },
    ],
  },
  {
    code: 'DA',
    en: 'Ad Dakhiliyah',
    ar: 'الداخلية',
    wilayats: [
      { code: 'NIZWA', en: 'Nizwa', ar: 'نزوى' },
      { code: 'SAMAIL', en: 'Samail', ar: 'سمائل' },
      { code: 'BAHLA', en: 'Bahla', ar: 'بهلاء' },
      { code: 'ADAM', en: 'Adam', ar: 'أدم' },
      { code: 'HAMRA', en: 'Al Hamra', ar: 'الحمراء' },
      { code: 'MANAH', en: 'Manah', ar: 'منح' },
      { code: 'IZKI', en: 'Izki', ar: 'إزكي' },
      { code: 'BIDBID', en: 'Bidbid', ar: 'بدبد' },
    ],
  },
  {
    code: 'BS',
    en: 'North Al Batinah',
    ar: 'شمال الباطنة',
    wilayats: [
      { code: 'SOHAR', en: 'Sohar', ar: 'صحار' },
      { code: 'SHINAS', en: 'Shinas', ar: 'شناص' },
      { code: 'LIWA', en: 'Liwa', ar: 'لوى' },
      { code: 'SAHAM', en: 'Saham', ar: 'صحم' },
      { code: 'KHABURAH', en: 'Al Khaburah', ar: 'الخابورة' },
      { code: 'SUWAYQ', en: 'As Suwayq', ar: 'السويق' },
    ],
  },
  {
    code: 'BJ',
    en: 'South Al Batinah',
    ar: 'جنوب الباطنة',
    wilayats: [
      { code: 'RUSTAQ', en: 'Ar Rustaq', ar: 'الرستاق' },
      { code: 'AWABI', en: 'Al Awabi', ar: 'العوابي' },
      { code: 'NAKHAL', en: 'Nakhal', ar: 'نخل' },
      { code: 'WADI_MAAWIL', en: 'Wadi Al Maawil', ar: 'وادي المعاول' },
      { code: 'BARKA', en: 'Barka', ar: 'بركاء' },
      { code: 'MUSANAAH', en: 'Al Musanaah', ar: 'المصنعة' },
    ],
  },
  {
    code: 'SS',
    en: 'North Ash Sharqiyah',
    ar: 'شمال الشرقية',
    wilayats: [
      { code: 'IBRA', en: 'Ibra', ar: 'إبراء' },
      { code: 'MUDAYBI', en: 'Al Mudaybi', ar: 'المضيبي' },
      { code: 'BIDIYAH', en: 'Bidiyah', ar: 'بدية' },
      { code: 'QABIL', en: 'Al Qabil', ar: 'القابل' },
      { code: 'WADI_BANI_KHALID', en: 'Wadi Bani Khalid', ar: 'وادي بني خالد' },
      { code: 'DIMA_TAIYYIN', en: 'Dima Wa At Taiyyin', ar: 'دماء والطائيين' },
    ],
  },
  {
    code: 'SJ',
    en: 'South Ash Sharqiyah',
    ar: 'جنوب الشرقية',
    wilayats: [
      { code: 'SUR', en: 'Sur', ar: 'صور' },
      { code: 'KAMIL_WAFI', en: 'Al Kamil Wal Wafi', ar: 'الكامل والوافي' },
      { code: 'JAALAN_BANI_BU_HASSAN', en: 'Jaalan Bani Bu Hassan', ar: 'جعلان بني بو حسن' },
      { code: 'JAALAN_BANI_BU_ALI', en: 'Jaalan Bani Bu Ali', ar: 'جعلان بني بو علي' },
      { code: 'MASIRAH', en: 'Masirah', ar: 'مصيرة' },
    ],
  },
  {
    code: 'ZA',
    en: 'Ad Dhahirah',
    ar: 'الظاهرة',
    wilayats: [
      { code: 'IBRI', en: 'Ibri', ar: 'عبري' },
      { code: 'YANQUL', en: 'Yanqul', ar: 'ينقل' },
      { code: 'DHANK', en: 'Dhank', ar: 'ضنك' },
    ],
  },
  {
    code: 'WU',
    en: 'Al Wusta',
    ar: 'الوسطى',
    wilayats: [
      { code: 'HAIMA', en: 'Haima', ar: 'هيماء' },
      { code: 'MAHOUT', en: 'Mahout', ar: 'محوت' },
      { code: 'DUQM', en: 'Ad Duqm', ar: 'الدقم' },
      { code: 'JAZIR', en: 'Al Jazir', ar: 'الجازر' },
    ],
  },
]

const BY_CODE = new Map(OMAN_GOVERNORATES.map((g) => [g.code, g]))

export function omanGovernorate(code: string): OmanGovernorate | undefined {
  return BY_CODE.get(code)
}

/** True when `wilayat` is one of `governorate`'s wilayats. */
export function isOmanWilayatOf(governorate: string, wilayat: string): boolean {
  return BY_CODE.get(governorate)?.wilayats.some((w) => w.code === wilayat) ?? false
}
